import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { categoryBadgeClass, categoryLabel } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { MapPin, Mail, Phone, ArrowLeft, ChevronLeft, ChevronRight, Copy, Lock, ShieldAlert, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import FeatureAdDialog from "@/components/FeatureAdDialog";

export default function AdDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [contactVisible, setContactVisible] = useState(false);
  const [featureOpen, setFeatureOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/ads/${id}`)
      .then((r) => setAd(r.data))
      .catch(() => setAd(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="max-w-7xl mx-auto p-10 text-[#475569]">Cargando anuncio...</div>;
  }
  if (!ad) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-center">
        <h2 className="font-serif text-3xl">Anuncio no encontrado</h2>
        <Link to="/" className="mt-4 inline-block text-[#3B5FB8] link-underline">Volver a la portada</Link>
      </div>
    );
  }

  const images = ad.images?.length ? ad.images : [];
  const isOwner = user && user.id && user.id === ad.owner_id;

  const revealContact = async () => {
    if (!user || !user.id) {
      toast.error("Inicia sesión para ver los datos de contacto");
      return;
    }
    // Fire-and-forget notification to the advertiser (non-blocking)
    api.post(`/ads/${id}/notify-contact`).catch(() => {});
    setContactVisible(true);
  };

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar este anuncio? Esta acción es irreversible.")) return;
    await api.delete(`/ads/${ad.id}`);
    toast.success("Anuncio eliminado");
    navigate("/mis-anuncios");
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
        <button
          onClick={() => navigate(-1)}
          data-testid="detail-back"
          className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#3B5FB8] mb-6 link-underline"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        <div className="grid gap-8 lg:gap-12 lg:grid-cols-12 items-start">
          {/* Gallery */}
          <div className="lg:col-span-7 space-y-4">
            <div className="relative aspect-[4/3] bg-white border border-[#E2E8F0] overflow-hidden">
              {images[current] ? (
                <img
                  src={fileUrl(images[current])}
                  alt={ad.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#64748B]">Sin imagen</div>
              )}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrent((c) => (c - 1 + images.length) % images.length)}
                    data-testid="gallery-prev"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 border border-[#E2E8F0] flex items-center justify-center hover:bg-white"
                    aria-label="Anterior"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrent((c) => (c + 1) % images.length)}
                    data-testid="gallery-next"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 border border-[#E2E8F0] flex items-center justify-center hover:bg-white"
                    aria-label="Siguiente"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`aspect-square border overflow-hidden ${
                      i === current ? "border-[#3B5FB8]" : "border-[#E2E8F0]"
                    }`}
                  >
                    <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5 lg:sticky lg:top-24 space-y-5">
            <div className="bg-white border border-[#E2E8F0] p-6 lg:p-8 space-y-5">
              <div className="flex items-center justify-between">
                <span
                  className={`inline-block px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.14em] border ${categoryBadgeClass(
                    ad.category,
                  )}`}
                >
                  {categoryLabel(ad.category)}
                </span>
                <span className="text-xs font-mono uppercase tracking-wider text-[#64748B]">
                  {new Date(ad.created_at).toLocaleDateString("es-ES")}
                </span>
              </div>
              <h1 data-testid="detail-title" className="font-serif text-3xl lg:text-4xl font-semibold text-[#1A1A1A] leading-tight">
                {ad.title}
              </h1>
              <div className="flex items-center gap-2 text-[#475569]">
                <MapPin className="w-4 h-4 text-[#3B5FB8]" />
                <span data-testid="detail-location">
                  {ad.city}
                  {ad.country ? `, ${ad.country}` : ""}
                  {ad.location ? ` · ${ad.location}` : ""}
                </span>
              </div>
              <div className="pt-4 border-t border-[#EEF2F7]">
                <p className="text-xs font-mono uppercase tracking-widest text-[#3B5FB8] mb-1">Precio</p>
                <p data-testid="detail-price" className="font-serif text-4xl font-semibold text-[#1A1A1A]">
                  {ad.price}
                </p>
              </div>

              {isOwner ? (
                <div className="space-y-2 pt-2">
                  {ad.is_featured && ad.featured_until && new Date(ad.featured_until) > new Date() ? (
                    <div className="border border-[#94A3B8] bg-[#EEF2F7] p-3 flex items-start gap-2" data-testid="already-featured-badge">
                      <Sparkles className="w-4 h-4 text-[#3B5FB8] flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-[#1A1A1A]">
                        <strong>Anuncio destacado activo</strong> hasta {new Date(ad.featured_until).toLocaleDateString("es-ES")}.
                      </div>
                    </div>
                  ) : (
                    <Button
                      data-testid="feature-ad-button"
                      onClick={() => setFeatureOpen(true)}
                      className="w-full bg-gradient-to-r from-[#3B5FB8] to-[#1E3A8A] hover:from-[#2E4A94] hover:to-[#152B6B] text-[#F8FAFC] rounded-none h-11 font-medium"
                    >
                      <Sparkles className="w-4 h-4 mr-2" /> Destacar este anuncio
                    </Button>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      data-testid="edit-ad-button"
                      onClick={() => navigate(`/publicar?editar=${ad.id}`)}
                      className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] rounded-none h-11"
                    >
                      Editar
                    </Button>
                    <Button
                      data-testid="delete-ad-button"
                      onClick={remove}
                      variant="outline"
                      className="rounded-none h-11 border-[#CBD5E1] hover:border-red-500 hover:text-red-700"
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Security warning */}
                  <div className="border border-[#A3B8DA] bg-[#EEF4FB] p-3 flex gap-2 items-start" data-testid="detail-security-warning">
                    <ShieldAlert className="w-4 h-4 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[#1A1A1A] leading-relaxed">
                      <strong>No compartas datos bancarios</strong> ni realices pagos por este medio.
                      buscocompartir no responde de posibles fraudes.{" "}
                      <Link to="/aviso-legal" className="link-underline text-[#1E3A8A] font-semibold">
                        Ver aviso legal
                      </Link>
                    </p>
                  </div>
                  {contactVisible ? (
                <div data-testid="contact-info" className="space-y-3 pt-2">
                  <button
                    onClick={() => copy(ad.contact_email, "Email")}
                    className="w-full flex items-center justify-between border border-[#CBD5E1] p-3 hover:border-[#3B5FB8] text-left group"
                    data-testid="contact-email-copy"
                  >
                    <span className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                      <Mail className="w-4 h-4 text-[#3B5FB8]" /> {ad.contact_email}
                    </span>
                    <Copy className="w-4 h-4 text-[#64748B] group-hover:text-[#3B5FB8]" />
                  </button>
                  {ad.contact_phone && (
                    <button
                      onClick={() => copy(ad.contact_phone, "Teléfono")}
                      className="w-full flex items-center justify-between border border-[#CBD5E1] p-3 hover:border-[#3B5FB8] text-left group"
                      data-testid="contact-phone-copy"
                    >
                      <span className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                        <Phone className="w-4 h-4 text-[#3B5FB8]" /> {ad.contact_phone}
                      </span>
                      <Copy className="w-4 h-4 text-[#64748B] group-hover:text-[#3B5FB8]" />
                    </button>
                  )}
                </div>
              ) : (
                <Button
                  data-testid="reveal-contact-button"
                  onClick={revealContact}
                  className="w-full bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] rounded-none h-12 font-medium tracking-wide"
                >
                  {user && user.id ? "Mostrar contacto" : (<><Lock className="w-4 h-4 mr-2" /> Inicia sesión para contactar</>)}
                </Button>
              )}
                </>
              )}

              <div className="pt-4 border-t border-[#EEF2F7]">
                <p className="text-xs font-mono uppercase tracking-widest text-[#3B5FB8] mb-1">Publicado por</p>
                <p className="text-sm text-[#1A1A1A] font-semibold">{ad.owner_name || "Anunciante"}</p>
              </div>
            </div>
          </aside>

          {/* Description */}
          <div className="lg:col-span-7 bg-white border border-[#E2E8F0] p-6 lg:p-10">
            <p className="eyebrow mb-3">Descripción</p>
            <div
              data-testid="detail-description"
              className="prose prose-neutral max-w-none text-[#1A1A1A] whitespace-pre-wrap leading-relaxed text-[15px]"
            >
              {ad.description}
            </div>
          </div>
        </div>
      </div>
      <FeatureAdDialog open={featureOpen} onOpenChange={setFeatureOpen} adId={ad.id} />
    </div>
  );
}
